<template>
  <div>
    <div
      class="flex flex-col justify-center items-center w-full h-screen"
      v-if="!error && verified && !success"
    >
      <p class="text-white text-4xl sm:text-5xl p-4 sm:p-8 border-grey font-light text-center">
        <i class="fa fa-check text-brand"></i> Invitation Verified
      </p>

      <div class="border border-grey flex flex-col w-5/6 md:w-1/2">
        <form @submit.prevent="submit">
          <div class="text-center bg-white p-8">
            <div class="md:w-5/6 mx-auto">
              <img
                :src="'/images/' + vault_type + '-sign-up-header.png'"
                alt=""
              >
              <p class="text-lg sm:text-2xl mt-6 sm:px-4">
                You have been invited to the following vault(s):
              </p>
              <ul class="list-disc list-inside text-left mt-4 text-grey-darker">
                <li v-for="(name, idx) in vaultNames" :key="idx">{{ name }}</li>
              </ul>
              <p v-if="new_user" class="text-lg sm:text-2xl mt-6 sm:px-4">
                Please create a unique login password below
              </p>
            </div>
          </div>
          <div v-if="new_user" class="text-center bg-grey-light p-8">
            <div class="w-full sm:w-5/6 mx-auto">
              <div class="mb-8">
                <label class="tracking-wide block text-black font-medium mb-2 text-center" for="password">
                  Password
                </label>
                <input
                  id="password"
                  ref="password"
                  v-model="password"
                  type="password"
                  name="password"
                  class="appearance-none border-grey border rounded w-full sm:w-5/6 py-2 px-3 text-grey-darker"
                  :class="{ 'border-red': errors.has('password') }"
                  v-validate="'required|min:8'"
                >
                <div v-show="errors.has('password')" class="text-red text-sm mt-2">
                  {{ errors.first('password') }}
                </div>
              </div>
              <div class="sm:mb-8">
                <label class="tracking-wide block text-black mb-2 text-center" for="confirm_password">
                  Confirm Password
                </label>
                <input
                  id="confirm_password"
                  v-model="repeat_password"
                  type="password"
                  name="confirm_password"
                  class="appearance-none border-grey border rounded w-full sm:w-5/6 py-2 px-3 text-grey-darker"
                  :class="{ 'border-red': errors.has('confirm_password') }"
                  v-validate="'required|confirmed:password'"
                >
                <div v-show="errors.has('confirm_password')" class="text-red text-sm mt-2">
                  {{ errors.first('confirm_password') }}
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white text-center py-6">
            <button
              type="submit"
              class="border border-grey bg-grey-darker rounded-lg py-4 px-8 text-white hover:bg-grey"
              @click.prevent="submit"
            >
              <span v-if="submit_invitation">
                <i class="fa fa-spinner fa-spin"></i> Please wait...
              </span>
              <span v-else>Continue</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="error" class="flex flex-col justify-center items-center w-full min-h-screen p-4">
      <div class="text-center max-w-md">
        <p class="text-red text-xl font-medium">{{ error }}</p>
        <button
          class="mt-4 px-4 py-2 border border-grey rounded hover:bg-grey-lighter"
          @click="redirectToLogin"
        >
          Go to Log In
        </button>
      </div>
    </div>

    <div v-if="!error && !verified && !success" class="flex flex-col justify-center items-center w-full min-h-screen">
      <p class="text-white"><i class="fa fa-circle-notch fa-spin"></i> Verifying...</p>
    </div>

    <div v-if="success" class="flex flex-col justify-center items-center w-full min-h-screen">
      <p class="text-white text-2xl font-medium"><i class="fa fa-check text-brand"></i> All invitations accepted</p>
      <p class="text-white mt-4"><i class="fa fa-circle-notch fa-spin"></i> Redirecting...</p>
    </div>

    <sweet-modal overlay-theme="dark" icon="error" ref="error_modal" hide-close-button>
      <p class="font-medium">Error</p>
      <p class="mt-4 text-grey-darker">{{ prompt }}</p>
      <button class="mt-4 px-4 py-2 border rounded" @click="$refs.error_modal.close()">OK</button>
    </sweet-modal>
  </div>
</template>

<script>
export default {
  name: 'AcceptAllInvitation',

  props: {
    initialToken: { type: String, default: null },
  },

  data() {
    return {
      token: null,
      verified: false,
      new_user: false,
      vaultNames: [],
      error: null,
      password: '',
      repeat_password: '',
      submit_invitation: false,
      success: false,
      prompt: '',
    };
  },

  mounted() {
    this.token = this.initialToken || (this.$route && this.$route.query.token);
    if (!this.token) {
      this.error = 'Invalid or missing invitation link.';
      return;
    }
    this.verifyToken();
  },

  methods: {
    verifyToken() {
      axios
        .post('/api/onboarding/verify-bulk', { token: this.token })
        .then(({ data }) => {
          this.verified = true;
          this.new_user = data.is_new_user;
          this.vaultNames = data.vault_names || [];
        })
        .catch((err) => {
          this.error = (err.response && err.response.data && err.response.data.message) || 'Invalid or expired link.';
        });
    },

    submit() {
      if (this.new_user) {
        this.$validator.validateAll().then((valid) => {
          if (valid) {
            this.acceptAll();
          }
        });
      } else {
        this.acceptAll();
      }
    },

    acceptAll() {
      this.submit_invitation = true;
      const payload = { token: this.token };
      if (this.new_user && this.password) {
        payload.password = this.password;
      }
      axios
        .post('/api/onboarding/consume-invitation-bulk', payload)
        .then((resp) => {
          this.success = true;
          if (this.new_user && this.password) {
            this.autoLogin(resp.data.email);
          } else {
            window.location.href = '/auth-login';
          }
        })
        .catch((err) => {
          this.submit_invitation = false;
          const data = err.response && err.response.data;
          this.prompt = (data && data.message) || 'Something went wrong. Please try again.';
          if (data && data.is_new_user && !this.password) {
            this.prompt = 'Password is required (min 8 characters).';
          }
          this.$refs.error_modal.open();
        });
    },

    autoLogin(email) {
      axios
        .post('/api/login', { email, password: this.password })
        .then(() => {
          window.location.href = (window.app_url || '') + '#/vaults';
        })
        .catch(() => {
          this.submit_invitation = false;
          this.success = false;
          this.prompt = 'Login failed. Please try logging in manually.';
          this.$refs.error_modal.open();
        });
    },

    redirectToLogin() {
      window.location.href = '/auth-login';
    },
  },

  computed: {
    vault_type() {
      return window.vault_type || 'strata';
    },
  },
};
</script>
